import React, { useState } from 'react';
import { useHistory, Link as RouterLink } from 'react-router-dom';
import { EllipsisVerticalIcon, PhotoIcon, CalendarIcon } from '@heroicons/react/24/outline';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import PageDeleteDialog from '@dashboard/components/PageDeleteDialog';
import getCheckoutUrl from '@dashboard/services/getCheckoutUrl';
import getConversionRate from '@dashboard/services/getConversionRate';
import { confirm } from '@dashboard/lib/confirm';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';
import { fetcher } from '@dashboard/services/request';
import { pagesKey, pageKey } from '@dashboard/lib/queryKeys';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { formatEventDate } from '@dashboard/lib/format-date';

import { Button } from '@dashboard/components/ui/button';
import EditPageNameDialog from './EditPageNameDialog';
import copy from 'copy-to-clipboard';
import DuplicatePageDialog from './DuplicatePageDialog';
import Statistics from './Statistic';
import { Badge } from './ui/badge';
import StatusBadge from './StatusBadge';
import checkIsEventSoldOut from '@dashboard/services/event/checkIsEventSoldOut';
import getRecoveredTooltipText from '../services/abandonment/getRecoveredTooltipText';
import ViewInAdminMenuItem from './ViewInAdminMenuItem';

const EventListItem = ({ page, refetch, isMainView = false, isGrid = false }) => {
  const { seller, sellerId, token } = useAuth();
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [editPageNameDialogOpen, setEditPageNameDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const history = useHistory();

  const { useDeletePage } = usePageQueries();
  const deletePage = useDeletePage();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const checkoutUrl = getCheckoutUrl(page, seller);

  async function handleDeletePage() {
    const proceed = await confirm('Are you sure you want to archive this event?', {
      description: 'This event will stop working and it will no longer be able to accept bookings.',
      confirm: 'Archive',
    });

    if (!proceed) return;

    const deleted = await deletePage.mutateAsync(page._id);

    if (!deleted) {
      return window.alert('Unable to delete event. Please try again or contact support.');
    }

    refetch();
  }

  const restorePageMutation = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/pages/${page._id}/restore`, token, {
        method: 'POST',
      }),
  });

  async function handleRestorePage() {
    const proceed = await confirm('Unarchive this event?', {
      description: 'This event will start working again and it will be able to receive bookings.',
      confirm: 'Unarchive',
    });

    if (!proceed) return;

    const restored = await restorePageMutation.mutateAsync();

    if (!restored) {
      return window.alert('Unable to unarchive this event. Please try again or contact support.');
    }

    toast({
      title: 'Event unarchived',
      status: 'success',
    });

    history.push(`/pages/${page._id}`);
    queryClient.invalidateQueries({ queryKey: pagesKey });
  }

  const conversionRate = getConversionRate(page.bookingCount, page.visitCount);

  const onNavigate = () => {
    const existingData = queryClient.getQueryData(pageKey(page._id));
    if (!existingData) {
      queryClient.setQueryData(pageKey(page._id), { ...page, seller });
    }
  };

  return (
    <div
      data-page-item
      className="relative h-[100px] w-full p-4 flex justify-start items-center transition-colors bg-white dark:bg-background hover:bg-gray-50 dark:hover:bg-muted border-b border-gray-200 dark:border-border last:border-b-0"
    >
      <RouterLink to={`/pages/${page._id}`} className="absolute inset-0 z-1" onClick={onNavigate} />

      <EditPageNameDialog
        isOpen={editPageNameDialogOpen}
        onClose={() => setEditPageNameDialogOpen(false)}
        checkout={page}
      />

      <div data-page-body className="grid xl:grid-cols-12 w-full gap-x-4">
        <div data-page-title className="xl:col-span-6 flex items-center min-w-0">
          <Thumbnail media={page.images} isGrid={isGrid} />

          <div className="truncate min-w-0">
            <div className="flex min-w-0 items-center gap-x-2">
              <h3 className="text-sm font-semibold truncate">{page.name}</h3>

              {page.testmode === true && (
                <Badge variant="warning" className="shrink-0 tracking-wide">
                  Test mode
                </Badge>
              )}
            </div>

            <div className="flex flex-col  gap-x-2 mt-0.5">
              {page.eventDetails.startDate && (
                <div data-page-date className="text-gray-700 dark:text-muted-foreground text-sm">
                  <div className="flex items-center gap-x-1 justify-start">
                    <CalendarIcon className="h-4 w-4" />

                    <div className="flex flex-col font-medium truncate">
                      {formatEventDate({
                        startDate: page.eventDetails.startDate,
                        endDate: page.eventDetails.endDate,
                        timezone: page.eventDetails.timezone,
                      }).join(' - ')}
                    </div>
                  </div>
                </div>
              )}

              <div className="space-x-2">
                {page.status === 'archived' && <Badge variant="info">Archived</Badge>}
                {page.eventDetails?.status === 'canceled' ? (
                  <StatusBadge status="canceled" label="Canceled" />
                ) : checkIsEventSoldOut(page) ? (
                  <StatusBadge status="sold_out" label="Sold out"></StatusBadge>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div data-page-stats className="hidden xl:flex col-span-6 items-center justify-between">
          <Statistics>
            <Statistics.Item colSpan={4}>
              <Statistics.Text tooltip="Gross revenue from payments before refunds">
                Revenue
              </Statistics.Text>
              <Statistics.Metric>
                {getPriceLabel({
                  amount: page.totalAmount,
                  currency: page.eventDetails.currency,
                })}
              </Statistics.Metric>
            </Statistics.Item>
            <Statistics.Item colSpan={4}>
              <Statistics.Text tooltip="Total number of tickets sold">Tickets sold</Statistics.Text>
              <Statistics.Metric>
                {page.ticketsSold || 0}
                {page.eventDetails?.capacity != null && `/${page.eventDetails?.capacity}`}
              </Statistics.Metric>
            </Statistics.Item>
            <Statistics.Item colSpan={3}>
              <Statistics.Text tooltip="Total number of event views">Views</Statistics.Text>
              <Statistics.Metric>{page.visitCount}</Statistics.Metric>
            </Statistics.Item>
            <Statistics.Item colSpan={3}>
              <Statistics.Text tooltip="Conversion Rate (conversions ÷ views × 100)">
                CVR
              </Statistics.Text>
              <Statistics.Metric>{conversionRate}%</Statistics.Metric>
            </Statistics.Item>

            <Statistics.Item colSpan={6}>
              <Statistics.Text
                tooltip={getRecoveredTooltipText(seller.checkoutAbandonment?.sendEmails)}
              >
                Recovered ({page.abandonedCartStats?.totalRecovered ?? 0}/
                {(page.abandonedCartStats?.totalAbandoned ?? 0) +
                  (page.abandonedCartStats?.totalRecovered ?? 0)}
                )
              </Statistics.Text>
              <Statistics.Metric>
                {getPriceLabel({
                  amount: page.abandonedCartStats?.totalRecoveredRevenue ?? 0,
                  currency: page.eventDetails?.currency,
                })}
              </Statistics.Metric>
            </Statistics.Item>
          </Statistics>

          <div className="relative z-10 mr-4 hidden lg:block">
            <Button asChild variant="outline">
              <RouterLink to={`/pages/${page._id}/customize`} onClick={onNavigate}>
                Edit
              </RouterLink>
            </Button>
          </div>
        </div>
      </div>

      {isMainView && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-lg"
              aria-label="More options"
              className="relative z-10 mr-2"
              data-page-menu
            >
              <EllipsisVerticalIcon className="size-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={8}>
            <DropdownMenuItem onSelect={() => setEditPageNameDialogOpen(true)}>
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                copy(checkoutUrl);
                toast({
                  title: 'Copied to clipboard',
                  status: 'success',
                });
              }}
            >
              Copy link
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setShowDuplicateDialog(true)}>
              Duplicate
            </DropdownMenuItem>
            {page.status === 'archived' ? (
              <DropdownMenuItem onSelect={handleRestorePage}>Unarchive</DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={handleDeletePage}>Archive</DropdownMenuItem>
            )}
            <DropdownMenuItem variant="destructive" onSelect={() => setDeleteDialogOpen(true)}>
              Delete
            </DropdownMenuItem>
            <ViewInAdminMenuItem pageId={page._id} />
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <PageDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        page={page}
        label="event"
      />

      <DuplicatePageDialog
        isOpen={showDuplicateDialog}
        page={page}
        onClose={() => setShowDuplicateDialog(false)}
        title="Duplicate event"
      />
    </div>
  );
};

function Thumbnail({ media, isGrid }) {
  const hasMedia = media && media.length > 0;

  if (hasMedia && media[0]) {
    const image = getImageSrcAndSrcSet({
      url: media[0].url,
      // Grid cards stretch the thumbnail to the card's width.
      ...(isGrid ? { width: 400, height: 300 } : { width: 80, height: 60 }),
    });

    return (
      <img
        data-page-thumb
        className="max-w-[80px] h-[60px] flex-shrink-0 object-cover mr-4 rounded bg-gray-50 dark:bg-card"
        src={image.src}
        srcSet={image.srcSet}
      />
    );
  }

  return (
    <div
      data-page-thumb
      className="max-w-[80px] h-[60px] mr-4 rounded relative w-full flex-shrink-0 bg-gray-50 dark:bg-card border border-gray-100 dark:border-border flex items-center justify-center"
    >
      <PhotoIcon className="text-gray-200 w-6" />
    </div>
  );
}

export default EventListItem;
